import type { PartnerResponsible } from 'shared/api/afterSalesApi'
import type { GetUserResponse } from 'shared/api/auth/requests'
import { INNOSCRIPTA_CHAMPION_RESPONSIBLE_NAME } from 'shared/constants/user'

import {
  DEFAULT_CASE_MANAGER,
  DEFAULT_CUSTOMER_SUCCESS,
  INTERNAL_SUPPORT_CONTACTS,
  RESPONSIBLE_TYPE_ID,
  SOFTWARE_SUPPORT_CONTACT
} from './InnoscriptaContacts.constants'
import type {
  ContactEntry,
  ContactGroup,
  ContactResponsible,
  ContactRoleKey,
  InnoscriptaContactsModel,
  InternalSupportContacts,
  ResolvedInternalSupportContacts
} from './InnoscriptaContacts.types'

const findByType = (
  responsibles: PartnerResponsible[],
  responsibleTypeId: number
): PartnerResponsible | undefined =>
  responsibles.find(
    responsible => responsible.responsible_type_id === responsibleTypeId
  )

export const findInnoscriptaChampion = (
  responsibles: PartnerResponsible[]
): PartnerResponsible | undefined =>
  responsibles.find(
    responsible =>
      responsible.responsible_type?.name ===
      INNOSCRIPTA_CHAMPION_RESPONSIBLE_NAME
  )

const toEntry = (
  responsible: ContactEntry['responsible'],
  roleKey: ContactRoleKey,
  isMainContact = false
): ContactEntry => ({ responsible, roleKey, isMainContact })

const hydrateContact = (
  contact: ContactResponsible,
  usersById: Map<number, GetUserResponse['data']>
): ContactResponsible | undefined => {
  const fetchedUser =
    contact.user.id === null ? undefined : usersById.get(contact.user.id)

  if (!fetchedUser) return undefined

  return {
    ...contact,
    user: {
      ...contact.user,
      id: fetchedUser.id,
      name: fetchedUser.name,
      avatar: fetchedUser.profile?.avatar ?? null,
      working_phone: fetchedUser.profile?.working_phone ?? null
    }
  }
}

export const hydrateInternalSupportContacts = (
  fetchedUsers: Array<GetUserResponse['data'] | undefined>
): ResolvedInternalSupportContacts => {
  const usersById = new Map<number, GetUserResponse['data']>()

  for (const fetchedUser of fetchedUsers) {
    if (fetchedUser) usersById.set(fetchedUser.id, fetchedUser)
  }

  const resolvedContacts: ResolvedInternalSupportContacts = {}

  for (const [contactKey, contact] of Object.entries(
    INTERNAL_SUPPORT_CONTACTS
  ) as Array<[keyof InternalSupportContacts, ContactResponsible]>) {
    const hydratedContact = hydrateContact(contact, usersById)

    if (hydratedContact) resolvedContacts[contactKey] = hydratedContact
  }

  return resolvedContacts
}

/**
 * Arranges the partner responsibles into the application groups the popup
 * shows. An optional Champion can be prepended as the external user's main
 * contact under General Support.
 *
 * The BSFZ and cashback groups always render, standing in a default person
 * when the partner has none. The funding group only lists the roles that came
 * back, and disappears entirely when neither did.
 */
export const groupPartnerResponsibles = (
  responsibles: PartnerResponsible[],
  generalSupportContact?: PartnerResponsible
): InnoscriptaContactsModel => {
  const caseManager =
    findByType(responsibles, RESPONSIBLE_TYPE_ID.CASE_MANAGER) ??
    DEFAULT_CASE_MANAGER
  const customerSuccess =
    findByType(responsibles, RESPONSIBLE_TYPE_ID.CUSTOMER_SUCCESS) ??
    DEFAULT_CUSTOMER_SUCCESS
  const afterSales = findByType(responsibles, RESPONSIBLE_TYPE_ID.AFTER_SALES)
  const salesRep = findByType(responsibles, RESPONSIBLE_TYPE_ID.SALES_REP)

  const fundingContacts = [
    afterSales && toEntry(afterSales, 'roles.after_sales'),
    salesRep && toEntry(salesRep, 'roles.sales_representative')
  ].filter((entry): entry is ContactEntry => Boolean(entry))

  const groups: ContactGroup[] = generalSupportContact
    ? [
        {
          titleKey: 'groups.general_support',
          contacts: [
            toEntry(generalSupportContact, 'roles.general_support', true)
          ]
        }
      ]
    : []

  groups.push(
    {
      titleKey: 'groups.bsfz_applications',
      contacts: [toEntry(caseManager, 'roles.case_manager', true)]
    },
    {
      titleKey: 'groups.cashback_applications',
      contacts: [toEntry(customerSuccess, 'roles.customer_success')]
    }
  )

  if (fundingContacts.length) {
    groups.push({
      titleKey: 'groups.unlock_more_funding',
      contacts: fundingContacts
    })
  }

  groups.push({
    titleKey: 'groups.software_support',
    contacts: [toEntry(SOFTWARE_SUPPORT_CONTACT, 'roles.software_support')]
  })

  return {
    mainContact: generalSupportContact ?? caseManager,
    groups
  }
}

/**
 * The contacts shown to innoscripta's own organization. The entries are
 * configured by user ID and hydrated from the auth users endpoint by the view.
 */
export const getGeneralSupportContacts = (
  supportContacts: ResolvedInternalSupportContacts
): InnoscriptaContactsModel | null => {
  const groups: ContactGroup[] = []

  const addGroup = (
    titleKey: ContactGroup['titleKey'],
    entries: Array<ContactEntry | undefined>
  ) => {
    const contacts = entries.filter((entry): entry is ContactEntry =>
      Boolean(entry)
    )

    if (contacts.length) groups.push({ titleKey, contacts })
  }

  addGroup(
    'groups.general_support',
    supportContacts.alihanIpekten
      ? [toEntry(supportContacts.alihanIpekten, 'roles.general_support', true)]
      : []
  )
  addGroup('groups.hr', [
    supportContacts.mariaStephan
      ? toEntry(supportContacts.mariaStephan, 'roles.hr_generalist')
      : undefined,
    supportContacts.muhammedHasanAnees
      ? toEntry(supportContacts.muhammedHasanAnees, 'roles.hr_application')
      : undefined
  ])
  addGroup('groups.time_tracking', [
    supportContacts.mohamedMosadRagab
      ? toEntry(
          supportContacts.mohamedMosadRagab,
          'roles.time_tracking_support'
        )
      : undefined,
    supportContacts.amirhosseinMoghadam
      ? toEntry(
          supportContacts.amirhosseinMoghadam,
          'roles.time_tracking_technical'
        )
      : undefined
  ])
  addGroup('groups.accounting', [
    supportContacts.vrushaliRai
      ? toEntry(supportContacts.vrushaliRai, 'roles.accounting_app')
      : undefined
  ])
  addGroup('groups.it', [
    supportContacts.sayedAbdulMusaddik
      ? toEntry(supportContacts.sayedAbdulMusaddik, 'roles.it_support')
      : undefined
  ])

  const fallbackMainContact = groups[0]?.contacts[0]?.responsible

  if (!fallbackMainContact) return null

  return {
    mainContact: supportContacts.alihanIpekten ?? fallbackMainContact,
    groups
  }
}
